import {
  DebtRepaymentProjection,
  displayCentsInDollars,
} from '@vgrubs/components';
import { isAxiosError } from 'axios';
import { DateTime } from 'luxon';

/** The backend takes a target of 1 to 36 months. */
export const TARGET_MONTHS_MIN = 1;
export const TARGET_MONTHS_MAX = 36;

/** "2026-08" -> "Aug 2026". */
export const formatMonth = (month: string) =>
  DateTime.fromFormat(month, 'yyyy-MM').toFormat('LLL yyyy');

/** "Jul - Sep 2026", or "Nov 2025 - Jan 2026" across a year. */
export const formatPeriod = (from: string, to: string) => {
  const start = DateTime.fromFormat(from, 'yyyy-MM');
  const end = DateTime.fromFormat(to, 'yyyy-MM');
  return start.year === end.year
    ? `${start.toFormat('LLL')} - ${end.toFormat('LLL yyyy')}`
    : `${start.toFormat('LLL yyyy')} - ${end.toFormat('LLL yyyy')}`;
};

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

/**
 * How long a projection takes to repay: "4.3 months", or days under a month
 * ("0.4 months" reads worse than "12 days"). Null when it collects nothing.
 */
export const durationLabel = (projection: DebtRepaymentProjection) => {
  const { estimatedDays: days, estimatedMonths: months } = projection;
  if (days == null || months == null) return null;
  if (months < 1) return plural(days, 'day', 'days');
  // One decimal at most, and none when it is whole: "6 months", not "6.0".
  return plural(Number(months.toFixed(1)), 'month', 'months');
};

/** "6 months" for a target, which is always whole. */
export const monthsLabel = (months: number) =>
  plural(months, 'month', 'months');

/**
 * The month a target of `months` from now ends in, New York: "Jun 2027".
 * The backend only takes a number of months; the form offers it as months
 * by name, which is how a repayment date is talked about.
 */
export const targetMonthName = (months: number) =>
  DateTime.now()
    .setZone('America/New_York')
    .plus({ months })
    .toFormat('LLL yyyy');

/**
 * The repay-by choices: every target the backend takes, by month name only.
 * "Oct 2029 (36 months)" did not fit the field beside the button (a select
 * draws in the wider system font); the result spells the months out.
 */
export const targetMonthOptions = () =>
  Array.from(
    { length: TARGET_MONTHS_MAX - TARGET_MONTHS_MIN + 1 },
    (_, index) => {
      const months = TARGET_MONTHS_MIN + index;
      return { months, label: targetMonthName(months) };
    }
  );

/** "the Jun 2027 target". */
export const targetLabel = (months: number) =>
  `the ${targetMonthName(months)} target`;

const wholeDollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/** "$4,028": for the month figures, where cents only cost width. */
export const displayWholeDollars = (cents: number) =>
  wholeDollars.format(Math.round(cents / 100));

export const perMonth = (cents: number) =>
  `${displayCentsInDollars(cents)} / month`;

/**
 * The three methods as rows; the value is "Off" when the method is. The vPay
 * Card is "On", or its daily limit ("$3.00 / day") when it has one.
 */
export const projectionMethods = (projection: DebtRepaymentProjection) => [
  {
    label: 'vPay Card',
    value: !projection.fromLocationBalance
      ? 'Off'
      : projection.dailyBalanceLimit != null
      ? `${displayCentsInDollars(projection.dailyBalanceLimit)} / day`
      : 'On',
    monthly: projection.fromLocationBalance
      ? projection.monthlyFromBalance
      : null,
  },
  {
    label: 'Per order',
    value:
      projection.perOrderAmount != null
        ? displayCentsInDollars(projection.perOrderAmount)
        : 'Off',
    monthly:
      projection.perOrderAmount != null ? projection.monthlyFromOrders : null,
  },
  {
    label: 'Per delivery',
    value:
      projection.perDeliveryAmount != null
        ? displayCentsInDollars(projection.perDeliveryAmount)
        : 'Off',
    monthly:
      projection.perDeliveryAmount != null
        ? projection.monthlyFromDeliveries
        : null,
  },
];

export const hasAnyMethod = (projection: DebtRepaymentProjection) =>
  projection.fromLocationBalance ||
  projection.perOrderAmount != null ||
  projection.perDeliveryAmount != null;

/**
 * Whether two projections are the same three methods and amounts, the daily
 * limit included.
 */
export const sameMethods = (
  a: DebtRepaymentProjection,
  b: DebtRepaymentProjection
) =>
  a.fromLocationBalance === b.fromLocationBalance &&
  (a.dailyBalanceLimit ?? null) === (b.dailyBalanceLimit ?? null) &&
  a.perOrderAmount === b.perOrderAmount &&
  a.perDeliveryAmount === b.perDeliveryAmount;

/**
 * What went wrong, and whether trying again could help. A 4xx is the
 * backend's sentence for the admin (nothing to repay, no such location, a
 * target out of range) and would fail the same way again; a 5xx is the AI
 * failing ("AI generation failed.") or a dropped connection, worth a retry.
 */
export const describeSuggestionError = (error: unknown) => {
  const response = isAxiosError(error) ? error.response : undefined;
  const sent = response?.data?.message;
  // class-validator answers a bad body with a list of sentences.
  const message = Array.isArray(sent) ? sent.join(' ') : sent;
  if (response && response.status < 500)
    return {
      message: message || 'No suggestion is available.',
      canRetry: false,
    };
  return {
    message: message || 'The AI suggestion failed.',
    canRetry: true,
  };
};
